import {
  ChatMessage,
  ChatService,
  ConversationParticipant,
  ConversationService,
  ConversationType,
  MessageType,
  TChatChannel,
} from '@talon/web/lib/api';
import { Box, Button, Card, CardFooter, CardHeader, Collapsible, Text, TextInput } from 'grommet';
import { Chat, Close } from 'grommet-icons';
import { kebabCase } from 'lodash';
import { observer } from 'mobx-react-lite';
import { useContext, useEffect, useRef, useState } from 'react';
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ThemeContext } from 'styled-components';

import { ITypingPayload, SupportChat } from '.';
import { IMentionCandidate, MessageList, UserInput } from '../../../components/ui';
import { useChatStore, useProjectStore, useTalentManagerStore, useUserStore } from '../../../context';

const RESERVED_MENTION_HANDLES: IMentionCandidate[] = [
  { id: 'bot', label: 'talonBot' },
  { id: 'support', label: 'talonSupport' },
];

const MESSAGE_PAGE_SIZE = 30;
const TYPING_PING_THROTTLE_MS = 2500;
const TYPING_INDICATOR_TTL_MS = 4000;

export const ChatWidget = observer(() => {
  const [query, setQuery] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [channels, setChannels] = useState<TChatChannel[]>([]);
  const [activeChannelId, setActiveChannelId] = useState<string>();
  const [participants, setParticipants] = useState<ConversationParticipant[]>([]);
  const [messageState, setMessageState] = useState<{
    conversationId?: string;
    messages: Array<Partial<ChatMessage>>;
    pending?: string;
  }>({ messages: [] });
  const [messageToSend, setMessageToSend] = useState<Partial<ChatMessage>>();
  const [showChat, setShowChat] = useState<boolean>(false);
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [startingY, setStartingY] = useState(0);
  const [timerStartedAt, setTimerStartedAt] = useState<Date | null>(null);
  const [timerNote, setTimerNote] = useState('');
  const [timerElapsedMs, setTimerElapsedMs] = useState(0);
  const [loggingTime, setLoggingTime] = useState(false);
  const [messagePage, setMessagePage] = useState(1);
  const [messageTotal, setMessageTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [typingUsers, setTypingUsers] = useState<Record<string, string>>({});
  const userStore = useUserStore();
  const tmStore = useTalentManagerStore();
  const chatStore = useChatStore();
  const projectStore = useProjectStore();
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const dragRef = useRef<HTMLDivElement>(null);
  const lastTypingSentAtRef = useRef(0);
  const lastSubmitRef = useRef<{ text: string; at: number } | null>(null);
  const typingTimeoutsRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const location = useLocation();
  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- styled-components ships its own @types/react, causing a Context type mismatch
  const theme = useContext(ThemeContext as React.Context<any>);

  const activeChannel = channels.find((c) => c.conversationId === activeChannelId);
  const activeEngagement = activeChannel?.engagementId
    ? tmStore.groupEngagements.find((e) => e.id === activeChannel.engagementId)
    : null;
  const creditBalance = activeEngagement
    ? activeEngagement.credit_minutes_purchased - activeEngagement.credit_minutes_used
    : null;
  const isCreditWarning = creditBalance !== null && creditBalance > 0 && creditBalance < 30;
  const totalUnread = channels.reduce((sum, c) => sum + c.unreadCount, 0);
  const mentionCandidates: IMentionCandidate[] = [
    ...participants
      .filter((p) => p.user_id !== userStore.user?.id)
      .map((p) => ({ id: p.user_id, label: `${p.user.first_name} ${p.user.last_name}`.trim() })),
    ...RESERVED_MENTION_HANDLES,
  ];

  const fetchChannels = async () => {
    try {
      const { channels: myChannels } = await ChatService.listMyChannels();
      setChannels(myChannels);
    } catch (error) {
      console.log('error fetching channels', error);
    }
  };

  useEffect(() => {
    if (userStore.user?.id) {
      tmStore.fetchGroupEngagements();
      fetchChannels();
    }
  }, [userStore.user?.id]);

  useEffect(() => {
    if (showChat) {
      fetchChannels();
    }
  }, [showChat]);

  useEffect(() => {
    if (dragRef.current) {
      setStartingY(dragRef.current.getBoundingClientRect().top);
    }
  }, [dragRef.current]);

  const sortMessagesAscending = (messages: Array<Partial<ChatMessage>>) =>
    messages.slice().sort((a, b) => {
      if (a.created_at && b.created_at) {
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      }
      return 0;
    });

  const selectChannel = async (channelId: string) => {
    setActiveChannelId(channelId);
    setMessagePage(1);
    setMessageTotal(0);
    setTypingUsers({});
    try {
      const [{ conversation }, { messages, total }] = await Promise.all([
        ConversationService.getConversation(channelId, 'participants'),
        ConversationService.listChatMessages(channelId, MESSAGE_PAGE_SIZE, 1),
      ]);
      setMessageState({ conversationId: channelId, messages: sortMessagesAscending(messages) });
      setMessageTotal(total);
      setParticipants(conversation.participants || []);
    } catch (error) {
      console.log('error loading channel history', error);
      setMessageState({ conversationId: channelId, messages: [] });
      setParticipants([]);
    }
    setChannels((prev) => prev.map((c) => (c.conversationId === channelId ? { ...c, unreadCount: 0 } : c)));
    ChatService.markChannelRead(channelId).catch((error) => console.log('error marking channel read', error));
  };

  const loadOlderMessages = async () => {
    if (!activeChannelId) return;
    setLoadingMore(true);
    try {
      const nextPage = messagePage + 1;
      const { messages: older } = await ConversationService.listChatMessages(
        activeChannelId,
        MESSAGE_PAGE_SIZE,
        nextPage
      );
      setMessageState((state) => ({ ...state, messages: [...sortMessagesAscending(older), ...state.messages] }));
      setMessagePage(nextPage);
    } catch (error) {
      console.log('error loading older messages', error);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!timerStartedAt) return;
    const interval = setInterval(() => setTimerElapsedMs(Date.now() - timerStartedAt.getTime()), 1000);
    return () => clearInterval(interval);
  }, [timerStartedAt]);

  const startTimer = () => {
    setTimerStartedAt(new Date());
    setTimerElapsedMs(0);
  };

  const stopAndLogTime = async () => {
    if (!timerStartedAt || !activeChannel?.engagementId) return;
    setLoggingTime(true);
    await tmStore.logManualTime({
      engagement_id: activeChannel.engagementId,
      category: 'Consultation',
      description: timerNote || 'Chat session',
      started_at: timerStartedAt.toISOString(),
      ended_at: new Date().toISOString(),
    });
    setLoggingTime(false);
    setTimerStartedAt(null);
    setTimerNote('');
    toast.success('Time logged.');
  };

  const sendTypingPing = () => {
    if (!activeChannelId) return;
    const now = Date.now();
    if (now - lastTypingSentAtRef.current < TYPING_PING_THROTTLE_MS) return;
    lastTypingSentAtRef.current = now;
    setMessageToSend({ type: MessageType.TYPING, conversation_id: activeChannelId } as Partial<ChatMessage>);
  };

  const handleQueryChange = (value: string) => {
    setQuery(value);
    sendTypingPing();
  };

  const handleTyping = (payload: ITypingPayload) => {
    if (payload.user_id === userStore.user?.id) return;
    if (typingTimeoutsRef.current[payload.user_id]) clearTimeout(typingTimeoutsRef.current[payload.user_id]);
    typingTimeoutsRef.current[payload.user_id] = setTimeout(() => {
      setTypingUsers((prev) => {
        const next = { ...prev };
        delete next[payload.user_id];
        return next;
      });
    }, TYPING_INDICATOR_TTL_MS);
    setTypingUsers((prev) => ({ ...prev, [payload.user_id]: payload.first_name }));
  };

  const formatElapsed = (ms: number) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Deep-link: a "you were mentioned" email links to `?openChat=<conversationId>` since chat has no
  // page route of its own; pick it up once on load and strip it from the URL.
  useEffect(() => {
    const openChatId = new URLSearchParams(location.search).get('openChat');
    if (openChatId) {
      chatStore.openChannel(openChatId);
      navigate(location.pathname, { replace: true });
    }
  }, []);

  // Same mechanism drives notification clicks (NotificationsMenu calls chatStore.openChannel directly).
  useEffect(() => {
    if (!chatStore.pendingChannelId) return;
    const channelId = chatStore.pendingChannelId;
    chatStore.clearPendingChannel();
    setShowChat(true);
    (async () => {
      if (!channels.length) await fetchChannels();
      await selectChannel(channelId);
    })();
  }, [chatStore.pendingChannelId]);

  // Side-rail default: the first time the widget is opened with nothing already selected, land
  // on the current project's General channel instead of an empty right-hand panel. Never
  // overrides a channel the user (or one of the mechanisms above) already selected.
  useEffect(() => {
    if (!showChat || activeChannelId || !channels.length) return;
    const currentProjectChannel = channels.find(
      (c) => c.conversationType === ConversationType.PROJECT && c.projectId === projectStore.currentProject?.id
    );
    if (currentProjectChannel) selectChannel(currentProjectChannel.conversationId);
  }, [showChat, channels, activeChannelId, projectStore.currentProject?.id]);

  //handle form submission
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!activeChannelId) return;

    const text = query.trim();
    if (!text) return;

    // Guards against a message getting persisted twice from a single logical send (observed in
    // QA: two identical ChatMessage rows, same conversation, milliseconds apart — the exact
    // client-side trigger wasn't reproducible, but no legitimate flow sends identical text twice
    // within a second, so this is a safe, cheap backstop regardless of root cause).
    const now = Date.now();
    if (lastSubmitRef.current?.text === text && now - lastSubmitRef.current.at < 1000) return;
    lastSubmitRef.current = { text, at: now };

    setLoading(true);
    setQuery('');
    setMessageToSend({
      type: MessageType.USER_MESSAGE,
      text,
      conversation_id: activeChannelId,
    });
    setLoading(false);
  }

  //prevent empty submissions
  const handleEnter = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && query) {
      handleSubmit(e);
    } else if (e.key == 'Enter') {
      e.preventDefault();
    }
  };

  if (!userStore.user?.id) {
    return null;
  }

  const onStart = (e: DraggableEvent, data: DraggableData) => {
    setDragStartPos({ x: data.x, y: data.y });
  };

  const onStop = (e: DraggableEvent, data: DraggableData) => {
    const deltaX = Math.abs(data.x - dragStartPos.x);
    const deltaY = Math.abs(data.y - dragStartPos.y);
    if (deltaX < 5 || deltaY < 5) {
      const height = Number(theme?.global?.size?.large?.substring(0, theme?.global?.size?.large?.length - 2));
      // click
      if (!showChat) {
        if (Math.abs(startingY + data.y) - height < 0) {
          setShowChat(true);
          setPosition({ x: data.x, y: height - startingY });
        } else {
          setShowChat(true);
        }
      }
    }
  };

  const projectChannels = channels.filter((c) => c.conversationType === ConversationType.PROJECT);
  const tmChannels = channels.filter((c) => c.conversationType === ConversationType.TALENT_MANAGER);
  // A TM can have more than one Support channel (one per agency group they're engaged by), so this
  // must render all of them, not just the first.
  const supportChannels = channels.filter((c) => c.conversationType === ConversationType.SUPPORT);
  // Project channels first so each project's bucket lists its "General" channel above that
  // project's per-engagement TM channels.
  const channelsByProject = [...projectChannels, ...tmChannels].reduce<Record<string, TChatChannel[]>>((acc, c) => {
    const key = c.projectName ?? 'Other';
    acc[key] = acc[key] ? [...acc[key], c] : [c];
    return acc;
  }, {});

  const renderChannelRow = (channel: TChatChannel) => (
    <Box
      key={channel.conversationId}
      direction="row"
      align="center"
      justify="between"
      pad={{ vertical: 'xsmall', horizontal: 'small' }}
      background={channel.conversationId === activeChannelId ? 'light-3' : undefined}
      hoverIndicator
      onClick={() => selectChannel(channel.conversationId)}
      style={{ cursor: 'pointer' }}
    >
      <Text size="small" weight={channel.unreadCount > 0 ? 'bold' : 'normal'}>
        {channel.name}
      </Text>
      {channel.unreadCount > 0 && (
        <Box background="status-critical" round pad={{ horizontal: 'xsmall' }}>
          <Text size="xsmall" color="white">
            {channel.unreadCount}
          </Text>
        </Box>
      )}
    </Box>
  );

  // Support agents already have a dedicated inbox for these conversations — a launcher to start
  // a new chat with support has no purpose on the page built for support to answer them.
  return !(
    location.pathname.includes('/admin') ||
    location.pathname.includes('/onboarding') ||
    location.pathname.includes('/support-inbox')
  ) ? (
      <Draggable
        bounds="parent"
        handle=".handle"
        nodeRef={dragRef}
        onStart={onStart}
        onStop={onStop}
        position={position}
        onDrag={(e, data) => setPosition({ x: data.x, y: data.y })}
        axis="both"
      >
        <Box
          pad="small"
          round={true}
          style={{ position: 'fixed', bottom: '10%', right: '5%', zIndex: '10' }}
          ref={dragRef}
        >
          {!showChat && (
            <Box
              background={{ color: 'brand-gradient', dark: true }}
              style={{ background: 'var(--brand-gradient)', cursor: 'move', position: 'relative' }}
              round={true}
              height="xxsmall"
              width="small"
              align="center"
              justify="center"
              elevation="medium"
              direction="row"
              gap="small"
              border={{ color: 'accent-1', size: 'small' }}
              className="handle"
            >
              <Text style={{ cursor: 'pointer' }}>Chat</Text>
              <Chat size="20px" enableBackground="true" style={{ cursor: 'pointer' }} />
              {totalUnread > 0 && (
                <Box
                  background="status-critical"
                  round
                  pad={{ horizontal: 'xsmall' }}
                  style={{ position: 'absolute', top: '-6px', right: '-6px' }}
                >
                  <Text size="xsmall" color="white">
                    {totalUnread}
                  </Text>
                </Box>
              )}
            </Box>
          )}
          <Collapsible direction="vertical" open={showChat}>
            <Card
              height="large"
              width="820px"
              background="light-1"
              border="all"
              elevation="medium"
              round={{ corner: 'top' }}
            >
              <Box direction="column" height="100%">
                <CardHeader
                  pad="small"
                  background={{ color: 'brand-gradient', dark: true }}
                  style={{ background: 'var(--brand-gradient)', cursor: 'move' }}
                  justify="between"
                  className="handle"
                  gap="small"
                >
                  <Text>Chat</Text>
                  <Close onClick={() => setShowChat(false)} cursor="pointer" />
                </CardHeader>
                <Box direction="row" flex>
                  <Box
                    width="160px"
                    flex={false}
                    overflow={{ vertical: 'auto' }}
                    background="light-2"
                    border={{ side: 'right', color: '#d9d9e3' }}
                  >
                    {Object.entries(channelsByProject).map(([projectName, channelsInBucket]) => (
                      <Box key={projectName} margin={{ top: 'xsmall' }}>
                        <Text size="xsmall" weight="bold" color="dark-4" margin={{ horizontal: 'small' }}>
                          {projectName}
                        </Text>
                        {channelsInBucket.map(renderChannelRow)}
                      </Box>
                    ))}
                    {supportChannels.length > 0 && (
                      <Box margin={{ top: 'small' }}>{supportChannels.map(renderChannelRow)}</Box>
                    )}
                    {!channels.length && (
                      <Text margin="small" size="small">
                      No conversations yet.
                      </Text>
                    )}
                  </Box>
                  <Box flex direction="column">
                    {!activeChannel ? (
                      <Box flex align="center" justify="center">
                        <Text size="small" color="dark-4">
                        Select a conversation
                        </Text>
                      </Box>
                    ) : (
                      <>
                        <Box
                          pad={{ horizontal: 'small', vertical: 'xsmall' }}
                          border={{ side: 'bottom', color: '#d9d9e3' }}
                          background={activeChannel.conversationType === ConversationType.SUPPORT ? 'accent-3' : 'accent-2'}
                        >
                          <Text weight="bold" size="small">
                            {activeChannel.name}
                          </Text>
                        </Box>
                        {isCreditWarning && (
                          <Box background="status-warning" pad={{ horizontal: 'small', vertical: 'xxsmall' }}>
                            <Text size="xsmall">
                            Low credits: {creditBalance}m remaining.{' '}
                              <a href={`/project/${kebabCase(activeChannel.projectName ?? '')}/talent-manager`}>
                              Buy more
                              </a>
                            </Text>
                          </Box>
                        )}
                        {messagePage * MESSAGE_PAGE_SIZE < messageTotal && (
                          <Box align="center" pad={{ vertical: 'xsmall' }}>
                            <Button
                              size="small"
                              label={loadingMore ? 'Loading…' : 'Load earlier messages'}
                              disabled={loadingMore}
                              onClick={loadOlderMessages}
                            />
                          </Box>
                        )}
                        <MessageList messages={messageState.messages} />
                        {Object.keys(typingUsers).length > 0 && (
                          <Text size="xsmall" color="dark-4" margin={{ horizontal: 'small', vertical: 'xsmall' }}>
                            {Object.values(typingUsers).join(', ')}{' '}
                            {Object.keys(typingUsers).length === 1 ? 'is' : 'are'} typing…
                          </Text>
                        )}
                        <CardFooter direction="column" align="center" gap="none">
                          {activeChannel.showLogTime && (
                            <Box
                              direction="row"
                              align="center"
                              gap="small"
                              width="100%"
                              pad={{ horizontal: 'small', vertical: 'xsmall' }}
                              background="light-2"
                            >
                              {!timerStartedAt ? (
                                <Button size="small" label="Log Time" onClick={startTimer} />
                              ) : (
                                <>
                                  <Text size="small" weight="bold">
                                    {formatElapsed(timerElapsedMs)}
                                  </Text>
                                  <TextInput
                                    size="small"
                                    placeholder="What did you work on? (optional)"
                                    value={timerNote}
                                    onChange={(e) => setTimerNote(e.target.value)}
                                  />
                                  <Button
                                    size="small"
                                    primary
                                    label={loggingTime ? 'Saving…' : 'Stop & Log'}
                                    disabled={loggingTime}
                                    onClick={stopAndLogTime}
                                  />
                                </>
                              )}
                            </Box>
                          )}
                          <SupportChat
                            setMessageState={setMessageState}
                            messageToSend={messageToSend}
                            conversationId={activeChannelId || ''}
                            onTyping={handleTyping}
                          />
                          <Box direction="row" width="100%">
                            <UserInput
                              handleSubmit={handleSubmit}
                              loading={loading}
                              handleEnter={handleEnter}
                              textAreaRef={textAreaRef}
                              query={query}
                              setQuery={handleQueryChange}
                              mentionCandidates={mentionCandidates}
                            />
                          </Box>
                        </CardFooter>
                      </>
                    )}
                  </Box>
                </Box>
              </Box>
            </Card>
          </Collapsible>
        </Box>
      </Draggable>
    ) : null;
});
